import { Dispatch, FC, SetStateAction, useRef } from 'react';

import {
  // debounce,
  startCase,
  uniqBy,
} from 'lodash';
import { useDropzone } from 'react-dropzone';
import Button from 'ui/components/common/Button';
import c, { SIZE } from 'utils/constants';
import { cn, useUploadToUploadCare } from 'utils/helpers';
import { Comment, COMMENT_TYPE } from 'utils/types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Checkbox, DateField, Textarea } from 'ui/components/form';
import {
  LOG_ACTIVITY_FORM,
  LOG_ACTIVITY_SCHEMA,
} from 'modules/account/contact/forms/schema';
import { Formik, type FormikHelpers } from 'formik';
import { useCreateNote } from 'modules/account/contact/hooks';
import { Dropdown } from 'ui/components/nav';

import UploadListItem from './UploadListItem';

interface Attachment {
  name: string;
  size: number;
  path: string;
  type: string;
}

interface Props {
  disabled?: boolean;
  size?: 'sm' | 'md';
  attachments?: Attachment[];
  setAttachments: Dispatch<SetStateAction<Attachment[]>>;
  maximized?: boolean;
  mode?: 'popover' | 'modal';
  commentType?: COMMENT_TYPE;
  closeComposer: () => void;
}

const ComposerLogActivity: FC<Props> = ({
  size = 'sm',
  attachments = [],
  setAttachments,
  mode = 'popover',
  commentType,
  closeComposer,
}) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { mutate: addNote, isLoading: createNoteLoading } = useCreateNote();
  const { mutateAsync: uploadFiles, isLoading: attachmentLoading } =
    useUploadToUploadCare();

  const showCommentTypeDropdown = location.pathname.includes('leads');

  console.log(showCommentTypeDropdown);

  const FORM_INIT = {
    logDate: new Date(),
    logType: commentType,
    specifyDateTime: false,
    note: '',
  } as LOG_ACTIVITY_FORM;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: c.MAX_UPLOAD_SIZE,
    noClick: true,
    noKeyboard: true,
    onDrop: (_acceptedFiles) => {
      setAttachments(
        (prevVal) =>
          uniqBy([...prevVal, ..._acceptedFiles], 'name') as Attachment[],
      );
    },
    onDropRejected: (fileRejections) => {
      const totalSize = fileRejections.reduce((acc, { errors, file }) => {
        if (errors[0].code === 'file-too-large') {
          return acc + file.size;
        }
        return acc;
      }, 0);
      if (fileRejections[0].errors[0].code === 'file-too-large') {
        console.log(totalSize);
        // showAttachmentsTooLargeModal(convertToMegabytes(totalSize));
      }
    },
  });

  const removeAttachment = (name: string) => {
    setAttachments((prevVal) => prevVal.filter((val) => !(val.name === name)));
  };

  const files = !!attachments?.length && (
    <div
      className={cn(
        'space-y-[6px]',
        'max-h-[200px]',
        'w-full',
        'overflow-x-hidden',
        'overflow-y-auto',
        attachments.length > 3 && 'pr-1',
      )}
    >
      {attachments?.map((file) => (
        <UploadListItem
          key={file.name}
          file={{
            ...file,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          }}
          size={SIZE.Small}
          onRemove={() => removeAttachment(file.name)}
        />
      ))}
    </div>
  );

  const onSubmit = async (
    value: LOG_ACTIVITY_FORM,
    { resetForm, setSubmitting }: FormikHelpers<LOG_ACTIVITY_FORM>,
  ) => {
    const castValues = LOG_ACTIVITY_SCHEMA.cast(value);

    const uploadedAttachments =
      attachments.length && (await uploadFiles(attachments as any[]));

    const source = () => {
      if (location.pathname.includes('leads')) {
        return 'leads';
      }

      if (location.pathname.includes('deals')) {
        return 'deals';
      }

      return 'companies';
    };
    addNote(
      {
        content: castValues.note,
        referenceId: params.id,
        source: startCase(source()),
        ...(castValues.specifyDateTime && {
          logCreatedAt: castValues.logDate?.toISOString(),
        }),
        type: castValues.logType,
        ...(uploadedAttachments && {
          attachments: uploadedAttachments.files.map((file) => ({
            fileUrl: file.cdnUrl,
            fileType: file.mimeType,
            fileName: file.originalFilename,
            fileSize: file.size,
            source: startCase(source()),
            ...(file.isImage && { fileThumbnail: file.cdnUrl }),
          })),
        }),
      } as Comment,
      {
        onSuccess: () => {
          resetForm();
          setSubmitting(false);
          closeComposer();
          setAttachments([]);
        },
      },
    );
  };
  const toolBarRef = useRef<HTMLDivElement>(null);

  return (
    <Formik
      initialValues={FORM_INIT}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={LOG_ACTIVITY_SCHEMA}
    >
      {({
        handleSubmit,
        setFieldValue,
        values,
        // isSubmitting,
        errors,
      }) => {
        return (
          <form onSubmit={handleSubmit} className="w-full">
            <div {...getRootProps()} className={cn(size)}>
              <input {...getInputProps()} />
              <div
                className={cn(
                  'rich-text-container',
                  'max-h-[600px] p-4',
                  mode === 'modal' && 'mode-modal',
                  mode === 'popover' && 'mode-popover',
                  'space-y-2',
                )}
              >
                {showCommentTypeDropdown && (
                  <div className="space-y-[10px] border-b border-b-separator-secondary pb-[14px]">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 w-full">
                        <div className="text-xs leading-5 tracking-wider">
                          Type
                        </div>
                        <Dropdown
                          className="flex-1 w-full"
                          selectedClassName="col-span-2 flex-1 !w-full"
                          label="Log Type"
                          onChange={(val) => {
                            setFieldValue('logType', val.value);
                            const source = () => {
                              if (location.pathname.includes('leads')) {
                                return 'leads';
                              }
                              return 'companies';
                            };
                            navigate(
                              `/contact/${source()}/${params.id}/notes?type=${val.value}`,
                            );
                          }}
                          options={[
                            { label: 'General', value: COMMENT_TYPE.GENERAL },
                            { label: 'Call', value: COMMENT_TYPE.CALL },
                            { label: 'SMS', value: COMMENT_TYPE.SMS },
                          ]}
                          size={SIZE.Small}
                          name="logType"
                          placeholder="Log Type"
                          value={commentType as string}
                          required
                        />
                      </div>
                      {commentType !== COMMENT_TYPE.GENERAL && (
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <div className="text-xs leading-5 tracking-wider">
                            Specify Date & time
                          </div>
                          <Checkbox
                            onChange={(e) => {
                              setFieldValue(
                                'specifyDateTime',
                                e.target.checked,
                              );
                            }}
                            checked={values.specifyDateTime}
                          />
                        </div>
                      )}
                    </div>
                    {values.specifyDateTime && (
                      <div className="flex items-center gap-2">
                        <div className="text-xs leading-5 tracking-wider">
                          Date & Time
                        </div>
                        <DateField
                          dateFormat={'MM/dd/yyyy hh:mm a'}
                          className={'flex-1'}
                          size={SIZE.Small}
                          showTimeSelect
                          name="logCreatedAt"
                          subText={errors.logDate ? errors.logDate : ''}
                          textType={errors.logDate ? 'danger' : 'default'}
                          required
                          selected={values.logDate && new Date(values.logDate)}
                          clearSelected={() => setFieldValue('logDate', '')}
                          onChange={(_, val) => {
                            setFieldValue('logDate', new Date(val as Date));
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <Textarea
                  isEmail
                  richText
                  name="email"
                  placeholder="Write your message here"
                  onChange={(d) => {
                    setFieldValue('note', d);
                  }}
                  value={values.note}
                  onClickAttachButton={open}
                  className={cn('rounded-[6px] border-separator-primary')}
                >
                  {files}
                </Textarea>
                <div>{files}</div>
                {isDragActive && (
                  <div
                    className={cn(
                      'absolute inset-y-0 left-0',
                      'bg-fill-tertiary',
                      'bg-opacity-90',
                      'h-full w-full',
                      'grid place-items-center',
                      'border-2 border-dashed border-secondary',
                    )}
                  >
                    <div className="text-lg">Drop Files Here</div>
                  </div>
                )}
              </div>
            </div>
            <div
              className="rich-text-toolbar relative w-fit"
              ref={toolBarRef}
            ></div>
            <div
              className={cn(
                'mx-[22px]',
                'py-4',
                'bg-fill-tertiary',
                'border-t-[0.5px]',
                'border-t-separator-primary',
                'flex items-center justify-between',
              )}
            >
              <Button
                type="submit"
                disabled={
                  !values.note || createNoteLoading || attachmentLoading
                }
                intent="secondary"
                size="sm"
                label="SAVE NOTE"
              />
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default ComposerLogActivity;
